import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Field, Th, Td, TableWrap } from "@/components/kit";
import { ImportButton } from "@/components/ImportButton";
import { errMsg, validMf } from "@/lib/format";

const TEXT = ["name", "code", "matricule_fiscal", "email", "phone", "address", "city", "postal_code", "payment_terms_days", "currency", "exemption_ref"];
const BOOL = ["vat_subject", "withholding_subject", "exempt"];

const PartnerDialog = ({ open, onClose, kind, initial, onSaved }) => {
  const { api, t } = useApp();
  const [f, setF] = useState({});
  useEffect(() => { setF(initial || { kind, vat_subject: true, payment_terms_days: 30, currency: "TND" }); }, [initial, kind, open]);
  const save = async () => {
    if (f.matricule_fiscal && !validMf(f.matricule_fiscal)) return toast.error("Matricule fiscal non conforme (ex. 1234567A/A/M/000)");
    try {
      const body = { ...f, kind, payment_terms_days: +f.payment_terms_days || 0 };
      if (initial?.id) await api.put(`/partners/${initial.id}`, body); else await api.post("/partners", body);
      toast.success(t("saved"));
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const lab = (k) => ({ matricule_fiscal: t("mf"), payment_terms_days: t("payment_terms") }[k] || t(k));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial?.id ? t("edit") : t(kind === "client" ? "new_client" : "new_supplier")}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {TEXT.map((k) => (
            <Field key={k} label={lab(k)} className={k === "name" || k === "address" ? "sm:col-span-2" : ""}>
              <Input data-testid={`partner-${k}`} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                placeholder={k === "matricule_fiscal" ? "1234567A/A/M/000" : ""} />
            </Field>
          ))}
          {BOOL.map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm"><Switch data-testid={`partner-${k}`} checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: v })} />{t(k)}</label>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>{t("cancel")}</Button><Button onClick={save} data-testid="partner-save">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Partners({ kind }) {
  const { api, t, can } = useApp();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/partners", { params: { kind, q } }).then((r) => setRows(r.data)), [api, kind, q]);
  useEffect(() => { load(); }, [load]);
  const rw = can("partners", true);
  const del = async (p) => {
    if (!window.confirm(`${t("delete")} ${p.name} ?`)) return;
    try { await api.delete(`/partners/${p.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={t("partner")} title={t(kind === "client" ? "clients" : "suppliers")}>
        <Input placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="w-56" data-testid="partner-search" />
        {rw && <ImportButton entity={kind === "client" ? "clients" : "suppliers"} onDone={load} />}
        {rw && <Button onClick={() => setEdit({})} data-testid="partner-new-btn"><Plus className="h-4 w-4 me-1" />{t(kind === "client" ? "new_client" : "new_supplier")}</Button>}
      </PageHeader>
      <Panel>
        {!rows ? <Loading /> : rows.length === 0 ? <Empty /> : (
          <TableWrap>
            <thead className="border-b border-slate-100"><tr><Th>{t("code")}</Th><Th>{t("name")}</Th><Th>{t("mf")}</Th><Th>{t("city")}</Th><Th>{t("email")}</Th><Th>TVA / RS</Th><Th /></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/70" data-testid={`partner-row-${p.code}`}>
                  <Td className="num text-xs">{p.code}</Td><Td className="font-medium">{p.name}</Td><Td className="num text-xs">{p.matricule_fiscal || "—"}</Td>
                  <Td>{p.city}</Td><Td>{p.email}</Td>
                  <Td className="text-xs">{p.exempt ? t("exempt") : p.vat_subject ? "TVA" : "—"}{p.withholding_subject ? " · RS" : ""}</Td>
                  <Td className="text-end whitespace-nowrap">
                    {rw && <Button variant="ghost" size="icon" onClick={() => setEdit(p)} data-testid={`partner-edit-${p.code}`}><Pencil className="h-4 w-4" /></Button>}
                    {rw && <Button variant="ghost" size="icon" onClick={() => del(p)} data-testid={`partner-delete-${p.code}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      <PartnerDialog open={!!edit} onClose={() => setEdit(null)} kind={kind} initial={edit?.id ? edit : null} onSaved={load} />
    </>
  );
}
